import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with credentials
const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper to format API error detail
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh')
};

// Members APIs
export const membersAPI = {
  getAll: (params) => api.get('/members', { params }),
  get: (id) => api.get(`/members/${id}`),
  create: (data) => api.post('/members', data),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id) => api.delete(`/members/${id}`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/members/${id}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCV: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/members/${id}/upload-cv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPhotoUrl: (id) => `${API}/api/members/${id}/photo`,
  getCVUrl: (id) => `${API}/api/members/${id}/cv`
};

// Applicants APIs
export const applicantsAPI = {
  getAll: (params) => api.get('/applicants', { params }),
  get: (id) => api.get(`/applicants/${id}`),
  create: (data) => api.post('/applicants', data),
  update: (id, data) => api.put(`/applicants/${id}`, data),
  convert: (id) => api.post(`/applicants/${id}/convert`),
  delete: (id) => api.delete(`/applicants/${id}`)
};

// Inquiries/Messages APIs
export const inquiriesAPI = {
  getAll: (params) => api.get('/inquiries', { params }),
  create: (data) => api.post('/inquiries', data),
  update: (id, data) => api.put(`/inquiries/${id}`, data),
  delete: (id) => api.delete(`/inquiries/${id}`)
};

// Organizational Units APIs
export const unitsAPI = {
  getAll: (params) => api.get('/units', { params }),
  get: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data)
};

// Projects APIs
export const projectsAPI = {
  getAll: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data)
};

// Tasks APIs
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data)
};

// Events APIs
export const eventsAPI = {
  getAll: (params) => api.get('/events', { params }),
  getPublic: () => api.get('/events/public'),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  rsvp: (id, status) => api.post(`/events/${id}/rsvp`, null, { params: { response_status: status } })
};

// Documents APIs
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getPublic: () => api.get('/documents/public'),
  get: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  upload: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' })
};

// Contributions APIs
export const contributionsAPI = {
  getAll: (params) => api.get('/contributions', { params }),
  create: (data) => api.post('/contributions', data)
};

// Finance APIs
export const financeAPI = {
  getAccounts: () => api.get('/finance/accounts'),
  getAccount: (id) => api.get(`/finance/accounts/${id}`),
  createAccount: (data) => api.post('/finance/accounts', data),
  getTransactions: (params) => api.get('/finance/transactions', { params }),
  createTransaction: (data) => api.post('/finance/transactions', data),
  getSummary: () => api.get('/finance/summary')
};

// Communications APIs
export const communicationsAPI = {
  getAll: () => api.get('/communications'),
  create: (data) => api.post('/communications', data)
};

// Governance APIs
export const governanceAPI = {
  getAll: (params) => api.get('/governance', { params }),
  create: (data) => api.post('/governance', data),
  update: (id, data) => api.put(`/governance/${id}`, data),
  delete: (id) => api.delete(`/governance/${id}`)
};

// Appointments APIs
export const appointmentsAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  create: (memberId, rank, office, decreeRef) => 
    api.post('/appointments', null, { params: { member_id: memberId, rank, office, decree_reference: decreeRef } })
};

// Audit Logs APIs
export const auditLogsAPI = {
  getAll: (params) => api.get('/audit-logs', { params })
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity')
};

// Reports APIs
export const reportsAPI = {
  membersByCommandery: () => api.get('/reports/members-by-commandery'),
  membersByPrefecture: () => api.get('/reports/members-by-prefecture'),
  projectsByUnit: () => api.get('/reports/projects-by-unit'),
  contributionSummary: () => api.get('/reports/contribution-summary')
};

// Content APIs
export const contentAPI = {
  get: (pageKey) => api.get(`/content/${pageKey}`),
  update: (pageKey, data) => api.put(`/content/${pageKey}`, data)
};

// News APIs
export const newsAPI = {
  getAll: (params) => api.get('/news', { params }),
  getPublic: () => api.get('/news/public'),
  create: (title, content, isPublic, newsType) => 
    api.post('/news', null, { params: { title, content, is_public: isPublic, news_type: newsType } })
};

// Gallery APIs
export const galleryAPI = {
  getAll: (params) => api.get('/gallery', { params }),
  getPublic: () => api.get('/gallery/public'),
  upload: (file, title, description, isPublic) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/gallery/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { title, description, is_public: isPublic }
    });
  },
  update: (id, data) => api.put(`/gallery/${id}`, data),
  delete: (id) => api.delete(`/gallery/${id}`),
  download: (id) => api.get(`/gallery/${id}/download`, { responseType: 'blob' })
};

// Leadership APIs
export const leadershipAPI = {
  getAll: () => api.get('/leadership'),
  create: (data) => api.post('/leadership', null, { params: data })
};

// Users APIs
export const usersAPI = {
  getAll: () => api.get('/users'),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } })
};

// Search API
export const searchAPI = {
  search: (q, entityTypes) => api.get('/search', { params: { q, entity_types: entityTypes } })
};

export default api;
